import React, { useState, useRef, useEffect, ReactNode } from 'react';

type DropdownPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface DropdownProps {
  children?: ReactNode;
  onToggle?: (isOpen: boolean) => void;
  placeholder?: string;
  position?: DropdownPosition;
  className?: string;
  trigger?: ReactNode;
}

export const Dropdown: React.FC<DropdownProps> = ({
  children,
  onToggle,
  placeholder = '選択してください',
  position = 'bottom-left',
  className = '',
  trigger
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Position classes for dropdown menu
  const positionClasses = {
    'top-left': 'top-0 left-0 -translate-y-full origin-bottom-left',
    'top-right': 'top-0 right-0 -translate-y-full origin-bottom-right',
    'bottom-left': 'top-full left-0 origin-top-left',
    'bottom-right': 'top-full right-0 origin-top-right'
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onToggle?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onToggle]);

  useEffect(() => {
    const handleCloseDropdown = () => {
      setIsOpen(false);
      onToggle?.(false);
    };

    const dropdown = dropdownRef.current;
    dropdown?.addEventListener('closeDropdown', handleCloseDropdown);

    return () => {
      dropdown?.removeEventListener('closeDropdown', handleCloseDropdown);
    };
  }, [onToggle]);

  const toggleDropdown = () => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    onToggle?.(newOpenState);
  };

  return (
    <div ref={dropdownRef} id="dropdown" className={`relative inline-block text-left ${className}`}>
      {/* Dropdown Trigger */}
      {trigger ? (
        <div onClick={toggleDropdown}>{trigger}</div>
      ) : (
        <button
          type="button"
          onClick={toggleDropdown}
          className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          {placeholder}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="ml-2 -mr-1 h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute z-10 ${positionClasses[position]} mt-1 rounded-lg bg-white`}
          style={{ width: 'max-content' }}>
          {/* <div className="py-1"> */}
          {children}
          {/* </div> */}
        </div>
      )}
    </div>
  );
};

// Dropdown Item Component
interface DropdownItemProps {
  children: ReactNode;
  onClick?: () => void;
  closeOnClick?: boolean;
}

export const DropdownItem: React.FC<DropdownItemProps> = ({ children, onClick, closeOnClick = true }) => {
  const handleClick = () => {
    onClick?.();
    if (closeOnClick) {
      // ドロップダウンを閉じるイベントを発火
      const dropdown = document.getElementById('dropdown');
      if (dropdown) {
        dropdown.dispatchEvent(new CustomEvent('closeDropdown'));
      }
    }
  };

  return <span onClick={handleClick}>{children}</span>;
};
